import { TableColumn } from './table-column.model';

/**
 * Column state as it is stored in the list config (config_json of a user
 * list, or the local storage for preconfigured lists). The list table reads
 * and writes this format, so saved column layouts keep working.
 */
export interface GridColumnState {
  visibleIndex?: number;
  dataField?: string;
  name?: string;
  dataType?: string;
  width?: number | string;
  visible?: boolean;
  sortOrder?: 'asc' | 'desc';
  sortIndex?: number;
}

export interface TableSort {
  field: string;
  order: 1 | -1;
}

/** A column of the list table with the sort it had in the saved state. */
export type StatefulColumn = TableColumn & {
  sortOrder?: 'asc' | 'desc';
  sortIndex?: number;
};

export interface TableState {
  /** All columns in display order, hidden ones included. */
  columns: StatefulColumn[];
  sort: TableSort | null;
}

const findState = (
  state: GridColumnState[],
  column: TableColumn,
  index: number
) =>
  column.field
    ? state.find((x) => (x.dataField || x.name) === column.field)
    : // Columns without data field (the link icons) are matched by position.
      state[index] && !state[index].dataField && !state[index].name
      ? state[index]
      : undefined;

/**
 * Applies a saved grid state to the column definitions: order by
 * visibleIndex, visibility and width from the state (a missing width in the
 * state removes the default width), and the sorting.
 */
export const applyColumnState = (
  columns: TableColumn[],
  state: GridColumnState[] | null | undefined
): TableState => {
  if (!state || !state.length) {
    return { columns: [...columns], sort: null };
  }
  const withState = columns.map((column, index) => {
    const entry = findState(state, column, index);
    const width = entry
      ? typeof entry.width === 'number'
        ? entry.width
        : typeof entry.width === 'string' && !isNaN(parseFloat(entry.width))
          ? parseFloat(entry.width)
          : undefined
      : column.width;
    return {
      index,
      order:
        entry && typeof entry.visibleIndex === 'number'
          ? entry.visibleIndex
          : index,
      column: {
        ...column,
        visible:
          entry && typeof entry.visible === 'boolean'
            ? entry.visible
            : column.visible,
        width,
        ...(entry && entry.sortOrder
          ? { sortOrder: entry.sortOrder, sortIndex: entry.sortIndex ?? 0 }
          : {}),
      } as StatefulColumn,
    };
  });
  const ordered = withState
    .sort((a, b) => a.order - b.order || a.index - b.index)
    .map((x) => x.column);
  const sorted = ordered
    .filter((column) => column.sortOrder && column.field)
    .sort((a, b) => (a.sortIndex ?? 0) - (b.sortIndex ?? 0));
  return {
    columns: ordered,
    sort: sorted.length
      ? {
          field: sorted[0].field as string,
          order: sorted[0].sortOrder === 'desc' ? -1 : 1,
        }
      : null,
  };
};

/**
 * The stored state of the columns: one entry per column in the order of the
 * column definitions, visibleIndex is the display position.
 */
export const toColumnState = (
  definitions: TableColumn[],
  state: TableState
): GridColumnState[] =>
  definitions.map((definition) => {
    const position = state.columns.findIndex((x) => x.key === definition.key);
    const column = state.columns[position] || definition;
    const sort = column.sortOrder
      ? { sortOrder: column.sortOrder, sortIndex: column.sortIndex ?? 0 }
      : {};
    return {
      visibleIndex: position,
      ...(column.field ? { dataField: column.field, name: column.field } : {}),
      dataType: column.dataType,
      ...(typeof column.width === 'number' ? { width: column.width } : {}),
      visible: column.visible,
      ...sort,
    };
  });

/** Sorting by one column replaces the sorting of the saved state. */
export const withSort = (
  state: TableState,
  sort: TableSort | null
): TableState => ({
  sort,
  columns: state.columns.map((column) =>
    sort && column.field === sort.field
      ? {
          ...column,
          sortOrder: sort.order === -1 ? 'desc' : 'asc',
          sortIndex: 0,
        }
      : { ...column, sortOrder: undefined, sortIndex: undefined }
  ),
});

/** Sorting of the request: the saved sort order of all sorted columns. */
export const toSortDescriptors = (
  columns: StatefulColumn[]
): { selector: string; desc: boolean }[] =>
  columns
    .filter((column) => column.sortOrder && column.field)
    .sort((a, b) => (a.sortIndex ?? 0) - (b.sortIndex ?? 0))
    .map((column) => ({
      selector: column.field as string,
      desc: column.sortOrder === 'desc',
    }));
